import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import IntervalInput, {
  parseIntervalInput,
  intervalToLatex,
} from "../../../../components/inputs/IntervalInput";
import NumberLineRanges from "../../../../components/visuals/NumberLineRanges";

/* ========== helpers ========== */
const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];
const randInt = (a, b) => a + Math.floor(Math.random() * (b - a + 1));

const SIGN = { lt: "<", le: "\\le" }; // lewe/prawe znaki: < lub ≤

// latex łącznej nierówności: a < x ≤ b
function betweenLatex(a, leftKind, b, rightKind) {
  return `${a}\\;${SIGN[leftKind]}\\;x\\;${SIGN[rightKind]}\\;${b}`;
}

// oczekiwany parsed (jak zwraca parseIntervalInput)
function expectedParsed(a, leftKind, b, rightKind) {
  return {
    kind: "intervals",
    intervals: [
      {
        left: a,
        right: b,
        leftClosed: leftKind === "le",
        rightClosed: rightKind === "le",
      },
    ],
  };
}

function sameSingleInterval(parsed, a, leftClosed, b, rightClosed) {
  if (!parsed || parsed.kind !== "intervals" || parsed.intervals.length !== 1)
    return false;
  const I = parsed.intervals[0];
  const eq = (x, y) => Math.abs(x - y) <= 1e-9;
  return (
    eq(I.left, a) &&
    eq(I.right, b) &&
    I.leftClosed === !!leftClosed &&
    I.rightClosed === !!rightClosed
  );
}

/* ========== generator ========== */
export function generateTask() {
  // Losujemy dwa różne końce i porządkujemy: a < b
  let a = randInt(-9, 8);
  let b = randInt(a + 1, 9);
  // Losujemy znaki po lewej/prawej: < lub ≤
  const leftKind = pick(["lt", "le"]);
  const rightKind = pick(["lt", "le"]);

  return {
    type: "InequalityBetweenTask",
    data: { a, b, leftKind, rightKind },
  };
}

/* ========== render treści (ta sama linia + niewielki odstęp) ========== */
export function renderTask({ data }) {
  const { a, b, leftKind, rightKind } = data;
  return (
    <div style={{ fontSize: 16, lineHeight: 1.5, marginBottom: 12 }}>
      <span>Zapisz nierówność w formie przedziału: </span>
      <span style={{ marginLeft: 8 }}>
        <InlineMath math={betweenLatex(a, leftKind, b, rightKind)} />
      </span>
    </div>
  );
}

/* ========== input — jak w Zadaniu 1 (x ∈ + IntervalInput) ========== */
export function renderInput(task, userAnswer, onChange, disabled) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-col gap-1" style={{ marginBottom: "0.6rem" }}>
        <div style={{ lineHeight: 1 }}>
          <InlineMath math={"x\\;\\in"} />
        </div>
        <IntervalInput
          value={userAnswer}
          onChange={onChange}
          disabled={disabled}
          asString
          previewWithX
          placeholder="np. (-3, 5], [−2, 7), [0, 4]"
        />
      </div>
    </div>
  );
}

/* ========== sprawdzanie ========== */
export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer) return false;
  const parsed =
    typeof userAnswer === "string" ? parseIntervalInput(userAnswer) : userAnswer.parsed;

  const leftClosed = data.leftKind === "le";
  const rightClosed = data.rightKind === "le";
  return sameSingleInterval(parsed, data.a, leftClosed, data.b, rightClosed);
}

/* ========== prezentacja odpowiedzi użytkownika ========== */
export function renderUserAnswer(userAnswer) {
  if (!userAnswer || (typeof userAnswer === "object" && !userAnswer.raw)) {
    return <i>(brak odpowiedzi)</i>;
  }
  const raw = typeof userAnswer === "string" ? userAnswer : userAnswer.raw;
  return <InlineMath math={`x\\in\\;${intervalToLatex(raw)}`} />;
}

/* ========== poprawna odpowiedź ========== */
export function renderAnswer({ data }) {
  const { a, b, leftKind, rightKind } = data;
  const lb = leftKind === "le" ? "[" : "(";
  const rb = rightKind === "le" ? "]" : ")";
  const raw = `${lb}${a}, ${b}${rb}`;
  return <InlineMath math={`x\\in\\;${intervalToLatex(raw)}`} />;
}

/* ========== wyjaśnienie (Krok 1: reguła; Krok 2: grafika) ========== */
export function renderExplanation({ data }) {
  const { a, b, leftKind, rightKind } = data;

  const leftClosed = leftKind === "le";
  const rightClosed = rightKind === "le";
  const lb = leftClosed ? "[" : "(";
  const rb = rightClosed ? "]" : ")";

  const rule =
    "Po lewej stronie „≤” oznacza nawias kwadratowy („domknięty”), „<” — okrągły („otwarty”). Analogicznie po prawej stronie.";

  const raw = `${lb}${a}, ${b}${rb}`;

  return (
    <div className="space-y-2">
      <p>
        <strong>Krok 1.</strong> {rule}
      </p>

      <div style={{ textAlign: "center", marginBottom: "0.5rem" }}>
        <BlockMath math={`x\\in\\;${intervalToLatex(raw)}`} />
      </div>

      <p>
        <strong>Krok 2.</strong> Zaznaczamy przedział na osi liczbowej.
      </p>

      <div style={{ maxWidth: 620, margin: "0 auto" }}>
        <NumberLineRanges
          lockPair
          showZero
          ranges={[
            {
              from: a,
              to: b,
              leftClosed,
              rightClosed,
              leftLabelLatex: `${a}`,
              rightLabelLatex: `${b}`,
            },
          ]}
        />
      </div>
    </div>
  );
}
